/**
 * S-33 «Доступ и сотрудники» — только владельцу. Приглашение — ссылкой с одноразовым payload
 * (ТЗ 6.2.6): сотрудник нажимает «Старт», бот показывает код ему и присылает вам заявку;
 * подтвердить можно здесь или присланными боту цифрами (кнопок, меняющих состояние, в боте нет — [19.2]).
 * Что видно сотруднику, решает владелец — переключатели прав (решение 13.10.2026).
 */
import { useState } from 'react'
import {
  fetchMembers, createInviteLink, revokeInviteLink, confirmMemberCode, revokeMemberAccess, setMemberPerm,
} from '../api/sections.js'
import { copyText, hapticSuccess, confirmAction, openTelegram } from '../telegram/call.js'
import { Icon } from '../components/icons/Icon.js'
import { Failed, Loading, NoShop, useAnnounce, useData, useScreenHead } from './common.js'
import { Kv, Sect, ToneTag } from './ui.js'

export function ScreenAccess(): React.JSX.Element {
  const { s, reload } = useData(fetchMembers)
  useScreenHead(undefined, null)
  const announce = useAnnounce()
  const [busy, setBusy] = useState(false)
  const [fresh, setFresh] = useState<{ link: string; note: string } | null>(null)
  const [codes, setCodes] = useState<Record<number, string>>({})
  if (s.kind === 'loading') return <Loading label="Загрузка доступа" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const run = async (f: () => Promise<{ ok: boolean; error?: { message: string } }>, ok: string): Promise<boolean> => {
    setBusy(true)
    const r = await f()
    setBusy(false)
    if (!r.ok) { announce({ kind: 'fail', text: r.error?.message ?? 'Не получилось. Попробуйте ещё раз.' }); return false }
    hapticSuccess(); announce({ kind: 'ok', text: ok }); reload(); return true
  }
  const invite = async (): Promise<void> => {
    setBusy(true)
    const r = await createInviteLink()
    setBusy(false)
    if (!r.ok) { announce({ kind: 'fail', text: r.error.message }); return }
    setFresh({ link: r.data.link, note: r.data.note }); hapticSuccess(); reload()
  }
  const share = (link: string): void =>
    openTelegram(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent('Приглашение в Селлер-Штаб магазина')}`)
  return (
    <div data-testid="screen-body">
      <Sect title="Магазин" />
      <section className="card"><Kv rows={[
        { label: d.store.title, text: null, bare: true, sub: `подключён ${d.store.since} · ключ Wildberries ${d.store.key}` },
      ]} /></section>

      <Sect title="Кто имеет доступ" aside={`${d.people.filter((p) => !p.pending).length}`} />
      <section className="card" data-testid="people">
        {d.people.map((p) => (
          <div className="person" key={p.tg} data-testid={'person-' + p.tg}>
            <div className="row">
              <span className="row-main">
                <span className="row-title">{p.name}</span>
                <span className="row-meta">{[p.username !== null ? '@' + p.username : null, p.name.startsWith('Telegram-ID') ? null : `Telegram-ID ${p.tg}`, `с ${p.since}`,
                  p.seen !== null ? `был ${p.seen}` : null].filter(Boolean).join(' · ')}</span>
                <span className="camp-tags"><ToneTag tone={p.pending ? 'attn' : 'neutral'} word={p.role} /></span>
              </span>
            </div>
            {p.pending && (
              <div className="person-act">
                <label className="row-meta" htmlFor={'code-' + p.tg}>Код, который видит сотрудник</label>
                <div className="person-line">
                  <input id={'code-' + p.tg} className="inp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={codes[p.tg] ?? ''}
                    data-testid={'code-' + p.tg} onChange={(e) => setCodes({ ...codes, [p.tg]: e.target.value.replace(/[^0-9]/g, '') })} />
                  <button type="button" className="btn" disabled={busy || (codes[p.tg] ?? '').length !== 6} data-testid={'confirm-' + p.tg}
                    onClick={() => { void run(() => confirmMemberCode(p.tg, codes[p.tg] ?? ''), `Доступ подтверждён: ${p.name}`) }}>Подтвердить</button>
                </div>
              </div>
            )}
            {p.rights !== null && (
              <div className="rights" role="group" aria-label={`Что видно: ${p.name}`} data-testid={'rights-' + p.tg}>
                <p className="row-meta rights-q">Что ему видно</p>
                {p.rights.map((r) => (
                  <div className="row row-form" key={r.key}>
                    <span className="row-main">
                      <span className="row-title" id={`right-${p.tg}-${r.key}`}>{r.label}</span>
                      <span className="row-meta">{r.hint}</span>
                    </span>
                    <button type="button" role="switch" aria-checked={r.on} aria-labelledby={`right-${p.tg}-${r.key}`} disabled={busy}
                      className={'switch' + (r.on ? ' switch-on' : '')} data-testid={`perm-${p.tg}-${r.key}`}
                      onClick={() => { void run(() => setMemberPerm(p.tg, r.key, !r.on), r.on ? `Закрыто: ${r.label.toLowerCase()}` : `Открыто: ${r.label.toLowerCase()}`) }}>
                      <span className="switch-knob" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {!p.primary && (
              <button type="button" className="link-inline person-revoke" disabled={busy} data-testid={'revoke-' + p.tg}
                onClick={() => { void confirmAction(`Отозвать доступ: ${p.name}? Сессии закроются сразу.`).then((yes) => {
                  if (yes) void run(() => revokeMemberAccess(p.tg), 'Доступ отозван')
                }) }}>Отозвать доступ</button>
            )}
          </div>
        ))}
      </section>

      <Sect title="Пригласить сотрудника" />
      <section className="card card-pad">
        <p className="body-text">Новому сотруднику открыты отзывы, остатки и реклама. Деньги магазина и отметку решений вы открываете сами — переключателями у сотрудника.</p>
        {fresh !== null && (
          <div className="invite-box" data-testid="invite-fresh">
            <p className="invite-link">{fresh.link}</p>
            <p className="row-meta">{fresh.note}</p>
            <div className="pager">
              <button type="button" className="btn btn-soft" onClick={() => { void copyText(fresh.link).then((ok) => { if (ok) { hapticSuccess(); announce({ kind: 'ok', text: 'Ссылка скопирована' }) } }) }}>
                <Icon name="action-copy" size={20} />Скопировать
              </button>
              <button type="button" className="btn btn-soft" onClick={() => share(fresh.link)}>Отправить в чат</button>
            </div>
          </div>
        )}
        <button type="button" className="btn btn-block gap-top-s" disabled={busy} data-testid="invite" onClick={() => { void invite() }}>
          {fresh === null ? 'Создать ссылку-приглашение' : 'Создать ещё одну'}
        </button>
        {d.invites.length > 0 && <>
          <p className="row-meta gap-top-s">Действующие приглашения:</p>
          {d.invites.map((i) => (
            <div className="person-line" key={i.id}>
              <span className="row-meta">до {i.until}</span>
              <button type="button" className="link-inline" disabled={busy} onClick={() => { void run(() => revokeInviteLink(i.id), 'Приглашение отозвано') }}>Отозвать</button>
            </div>
          ))}
        </>}
      </section>

      <Sect title="Данные и приватность" />
      <section className="card card-pad">
        {d.privacy.map((x, k) => <p className="body-text" key={k}>{x}</p>)}
      </section>
    </div>
  )
}
